import { useEffect, useRef, useState } from 'react'
import type { SecretMetadata, SecretScope } from '@/types'

export type SecretFormMode = 'create' | 'update'

interface Props {
  mode: SecretFormMode
  /** Provided only when mode === 'update' */
  secret?: SecretMetadata
  onSubmit: (data: { name: string; scope: SecretScope; scope_key: string; plaintext_value: string }) => Promise<void>
  onCancel: () => void
  submitting?: boolean
  error?: string | null
}

const SCOPE_OPTIONS: { value: SecretScope; label: string }[] = [
  { value: 'global', label: 'Global' },
  { value: 'site', label: 'Site' },
  { value: 'node', label: 'Node' },
]

export default function SecretForm({ mode, secret, onSubmit, onCancel, submitting, error }: Props) {
  const [name, setName] = useState(secret?.name ?? '')
  const [scope, setScope] = useState<SecretScope>(secret?.scope ?? 'global')
  const [scopeKey, setScopeKey] = useState(secret?.scope_key ?? 'global')
  const [plaintext, setPlaintext] = useState('')
  const [showPlaintext, setShowPlaintext] = useState(false)
  const plaintextRef = useRef<HTMLInputElement>(null)

  // Auto-fill scope_key when scope === 'global'
  useEffect(() => {
    if (scope === 'global') setScopeKey('global')
  }, [scope])

  // Security: clear plaintext from state when component unmounts
  useEffect(() => {
    return () => {
      setPlaintext('')
    }
  }, [])

  // Security: clear plaintext on blur of the input field
  // (keep the value for the session; clear on blur only if user explicitly
  // navigates away — we still need the value for submit)
  // The critical requirement is: once modal closes (unmount), value is gone.
  // unmount effect above covers that case.

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!plaintext.trim()) return
    await onSubmit({
      name: name.trim(),
      scope,
      scope_key: scopeKey.trim() || 'global',
      plaintext_value: plaintext,
    })
    // parent closes modal on success → unmount clears plaintext
  }

  const isUpdate = mode === 'update'

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      {error && (
        <div style={errStyle}>{error}</div>
      )}

      <Field label="Name" required>
        <input
          style={inputStyle}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isUpdate}
          placeholder="e.g. ftp_password"
          required
          autoComplete="off"
        />
      </Field>

      <Field label="Scope" required>
        <select
          style={inputStyle}
          value={scope}
          onChange={(e) => setScope(e.target.value as SecretScope)}
          disabled={isUpdate}
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {scope !== 'global' && (
        <Field label={scope === 'site' ? 'Site key' : 'Node ID'} required>
          <input
            style={inputStyle}
            value={scopeKey}
            onChange={(e) => setScopeKey(e.target.value)}
            disabled={isUpdate}
            placeholder={scope === 'site' ? 'e.g. site-a' : 'e.g. node-uuid'}
            required
            autoComplete="off"
          />
        </Field>
      )}

      <Field label={isUpdate ? 'New plaintext value' : 'Plaintext value'} required>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            ref={plaintextRef}
            style={{ ...inputStyle, flex: 1, fontFamily: showPlaintext ? 'inherit' : 'monospace' }}
            type={showPlaintext ? 'text' : 'password'}
            value={plaintext}
            onChange={(e) => setPlaintext(e.target.value)}
            placeholder="Enter secret value"
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPlaintext((v) => !v)}
            style={toggleBtnStyle}
            title={showPlaintext ? 'Hide' : 'Show'}
          >
            {showPlaintext ? 'Hide' : 'Show'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
          Value is visible only while this form is open. It will not be stored or displayed again.
        </div>
      </Field>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
        <button type="button" onClick={onCancel} style={secondaryBtnStyle} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" style={primaryBtnStyle} disabled={submitting || !plaintext.trim()}>
          {submitting ? 'Saving...' : isUpdate ? 'Replace value' : 'Create secret'}
        </button>
      </div>
    </form>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#c62828', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  boxSizing: 'border-box',
}

const errStyle: React.CSSProperties = {
  color: '#c62828',
  background: '#fdecea',
  border: '1px solid #f5c6c2',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 13,
  marginBottom: 14,
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const toggleBtnStyle: React.CSSProperties = {
  padding: '7px 12px',
  background: '#f0f0f4',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0,
}
